import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../context/LangContext';
import {
  FaHome, FaSearch, FaThLarge, FaEnvelope, FaInfoCircle,
  FaRobot, FaServer, FaCode, FaShieldAlt, FaGlobe,
  FaBullhorn, FaChartBar, FaChartLine, FaShareAlt,
  FaShoppingCart, FaMoneyBillWave, FaCreditCard,
  FaMobileAlt, FaGamepad, FaUserSecret, FaLink,
  FaBlog, FaSearch as FaSearchAlt, FaComments,
  FaTools, FaStar, FaHandshake, FaDatabase,
  FaEnvelopeOpenText, FaLock, FaImage, FaRocket,
  FaNetworkWired, FaDesktop, FaPuzzlePiece, FaGift,
  FaFileAlt, FaEye, FaWifi, FaMapMarkerAlt, FaWordpress,
  FaThumbtack, FaChevronRight
} from 'react-icons/fa';

const ICON_MAP = {
  FaRobot, FaServer, FaCode, FaShieldAlt, FaGlobe, FaBullhorn,
  FaChartBar, FaChartLine, FaShareAlt, FaShoppingCart, FaMoneyBillWave,
  FaCreditCard, FaMobileAlt, FaGamepad, FaUserSecret, FaLink, FaBlog,
  FaComments, FaTools, FaStar, FaHandshake, FaDatabase,
  FaEnvelopeOpenText, FaLock, FaImage, FaRocket, FaNetworkWired,
  FaDesktop, FaPuzzlePiece, FaGift, FaFileAlt, FaEye, FaWifi,
  FaMapMarkerAlt, FaWordpress, FaThLarge, FaHome, FaEnvelope, FaInfoCircle,
};

function getCategoryIcon(name, slug) {
  const lower = (name + ' ' + slug).toLowerCase();
  if (lower.includes('ai')) return FaRobot;
  if (lower.includes('seo')) return FaSearchAlt;
  if (lower.includes('sem') || lower.includes('ppc')) return FaChartLine;
  if (lower.includes('cdn')) return FaNetworkWired;
  if (lower.includes('vps') || lower.includes('服务器') || lower.includes('server') || lower.includes('cloud')) return FaServer;
  if (lower.includes('wordpress')) return FaWordpress;
  if (lower.includes('spy')) return FaEye;
  if (lower.includes('adult')) return FaShieldAlt;
  if (lower.includes('push')) return FaWifi;
  if (lower.includes('pop')) return FaDesktop;
  if (lower.includes('cod') || lower.includes('货到付款')) return FaShoppingCart;
  if (lower.includes('cpa') || lower.includes('cpc')) return FaBullhorn;
  if (lower.includes('mvas') || lower.includes('移动') || lower.includes('mobile')) return FaMobileAlt;
  if (lower.includes('博客') || lower.includes('blog')) return FaBlog;
  if (lower.includes('广告') || lower.includes('advertis')) return FaBullhorn;
  if (lower.includes('联盟') || lower.includes('alliance') || lower.includes('affiliate')) return FaHandshake;
  if (lower.includes('域名') || lower.includes('domain')) return FaGlobe;
  if (lower.includes('游戏') || lower.includes('game')) return FaGamepad;
  if (lower.includes('代理') || lower.includes('proxy') || lower.includes('浏览器') || lower.includes('browser')) return FaUserSecret;
  if (lower.includes('支付') || lower.includes('收款') || lower.includes('pay')) return FaCreditCard;
  if (lower.includes('变现') || lower.includes('monetiz')) return FaMoneyBillWave;
  if (lower.includes('流量') || lower.includes('traffic')) return FaChartBar;
  if (lower.includes('电商') || lower.includes('ecommerce') || lower.includes('淘')) return FaShoppingCart;
  if (lower.includes('素材') || lower.includes('material') || lower.includes('image')) return FaImage;
  if (lower.includes('着陆') || lower.includes('landing')) return FaFileAlt;
  if (lower.includes('社交') || lower.includes('social')) return FaShareAlt;
  if (lower.includes('邮件') || lower.includes('email') || lower.includes('mail')) return FaEnvelopeOpenText;
  if (lower.includes('追踪') || lower.includes('track')) return FaMapMarkerAlt;
  if (lower.includes('订阅') || lower.includes('subscri')) return FaRocket;
  if (lower.includes('解锁') || lower.includes('paywall') || lower.includes('unlock')) return FaLock;
  if (lower.includes('抽奖') || lower.includes('lottery') || lower.includes('gift')) return FaGift;
  if (lower.includes('搜索') || lower.includes('search')) return FaSearchAlt;
  if (lower.includes('工具') || lower.includes('tool')) return FaTools;
  if (lower.includes('热门') || lower.includes('popular') || lower.includes('推荐')) return FaStar;
  if (lower.includes('论坛') || lower.includes('forum')) return FaComments;
  if (lower.includes('网络') || lower.includes('network')) return FaNetworkWired;
  if (lower.includes('营销') || lower.includes('marketing')) return FaRocket;
  if (lower.includes('综合') || lower.includes('comprehensive')) return FaPuzzlePiece;
  if (lower.includes('后台') || lower.includes('backend')) return FaDatabase;
  if (lower.includes('软件') || lower.includes('software')) return FaCode;
  return FaThLarge;
}

const categoryGradients = [
  { from: '#6366f1', to: '#818cf8' },
  { from: '#22d3ee', to: '#67e8f9' },
  { from: '#f472b6', to: '#f9a8d4' },
  { from: '#34d399', to: '#6ee7b7' },
  { from: '#fbbf24', to: '#fde68a' },
  { from: '#a855f7', to: '#c084fc' },
  { from: '#fb923c', to: '#fdba74' },
  { from: '#60a5fa', to: '#93c5fd' },
  { from: '#f43f5e', to: '#fb7185' },
  { from: '#14b8a6', to: '#5eead4' },
];

function Sidebar({ categories, categoryTree, activeCategory, onCategoryClick, onSearchClick, onPinChange }) {
  const { t } = useLang();
  const [pinned, setPinned] = useState(() => localStorage.getItem('sidebar_pinned') === 'true');
  const [hovered, setHovered] = useState(false);
  const [expandedCats, setExpandedCats] = useState(new Set());

  const expanded = pinned || hovered;

  const togglePin = () => {
    const next = !pinned;
    setPinned(next);
    localStorage.setItem('sidebar_pinned', next.toString());
    if (onPinChange) onPinChange(next);
  };

  const toggleCatExpand = (e, catId) => {
    e.stopPropagation();
    setExpandedCats(prev => {
      const next = new Set(prev);
      if (next.has(catId)) next.delete(catId);
      else next.add(catId);
      return next;
    });
  };

  // Use tree data if available, otherwise fall back to flat list
  const treeData = categoryTree && categoryTree.length > 0 ? categoryTree : null;

  return (
    <>
      {expanded && !pinned && <div className="sidebar-overlay" onClick={() => setHovered(false)} />}

      <aside className={`sidebar ${expanded ? 'sidebar-expanded' : ''} ${pinned ? 'sidebar-pinned' : ''}`}
        onMouseEnter={() => { if (!pinned) setHovered(true); }}
        onMouseLeave={() => { if (!pinned) setHovered(false); }}
      >
        {/* Brand */}
        <div className="sidebar-brand">
          <div className="sidebar-logo-wrap">
            <img src="/logo.svg" alt="G" className="sidebar-logo-img" />
            <div className="sidebar-logo-pulse" />
          </div>
          <div className="sidebar-brand-text-group">
            <span className="sidebar-brand-text">GGLANCC</span>
            <span className="sidebar-brand-sub">Resource Hub</span>
          </div>
          <button
            className={`sidebar-pin-btn ${pinned ? 'pinned' : ''}`}
            onClick={togglePin}
            title={pinned ? t('unpinSidebar') : t('pinSidebar')}
          >
            <FaThumbtack />
          </button>
        </div>

        {/* Top Actions */}
        <nav className="sidebar-nav">
          <div className="sidebar-section">
            <button
              className={`sidebar-item sidebar-item-home ${!activeCategory ? 'active' : ''}`}
              onClick={() => { onCategoryClick(null); if (!pinned) setHovered(false); }}
            >
              <div className="sidebar-item-icon-wrap sidebar-home-icon">
                <FaHome />
              </div>
              <span className="sidebar-item-text">{t('home')}</span>
            </button>
            <button className="sidebar-item" onClick={() => { onSearchClick(); if (!pinned) setHovered(false); }}>
              <div className="sidebar-item-icon-wrap sidebar-search-icon">
                <FaSearch />
              </div>
              <span className="sidebar-item-text">{t('search')}</span>
              <kbd className="sidebar-kbd">⌘K</kbd>
            </button>
          </div>

          <div className="sidebar-divider">
            <span className="sidebar-divider-text">{t('categories')}</span>
          </div>

          {/* Category list — tree or flat */}
          <div className="sidebar-section sidebar-categories">
            {treeData ? (
              // Tree mode: parent categories with expandable children
              treeData.map((cat, i) => {
                const IconComponent = (cat.icon && ICON_MAP[cat.icon]) || getCategoryIcon(cat.name, cat.slug);
                const gradient = categoryGradients[i % categoryGradients.length];
                const isActive = activeCategory === cat.slug;
                const hasChildren = cat.children && cat.children.length > 0;
                const isCatExpanded = expandedCats.has(cat.id);

                return (
                  <div key={cat.id} className="sidebar-cat-group">
                    <button
                      className={`sidebar-item ${isActive ? 'active' : ''}`}
                      onClick={() => { onCategoryClick(cat.slug); if (!pinned) setHovered(false); }}
                      style={{ '--cat-from': gradient.from, '--cat-to': gradient.to }}
                    >
                      <div className="sidebar-item-icon-wrap" style={{
                        background: isActive ? `linear-gradient(135deg, ${gradient.from}22, ${gradient.to}22)` : undefined,
                        color: isActive ? gradient.from : undefined
                      }}>
                        <IconComponent />
                      </div>
                      <span className="sidebar-item-text">{cat.name}</span>
                      {cat.total_resources > 0 && (
                        <span className="sidebar-count" style={{
                          color: isActive ? gradient.from : undefined,
                          background: isActive ? `${gradient.from}15` : undefined
                        }}>
                          {cat.total_resources}
                        </span>
                      )}
                      {hasChildren && (
                        <span
                          className={`sidebar-expand-btn ${isCatExpanded ? 'sidebar-expand-open' : ''}`}
                          onClick={(e) => toggleCatExpand(e, cat.id)}
                        >
                          <FaChevronRight />
                        </span>
                      )}
                      {isActive && (
                        <div className="sidebar-active-indicator" style={{ background: gradient.from, boxShadow: `0 0 12px ${gradient.from}` }} />
                      )}
                    </button>

                    {/* Subcategories */}
                    {hasChildren && isCatExpanded && (
                      <div className="sidebar-subcats">
                        {cat.children.map(child => {
                          const ChildIcon = (child.icon && ICON_MAP[child.icon]) || getCategoryIcon(child.name, child.slug);
                          return (
                            <button
                              key={child.id}
                              className="sidebar-item sidebar-subcat-item"
                              onClick={() => { onCategoryClick(cat.slug); if (!pinned) setHovered(false); }}
                              style={{ '--cat-from': gradient.from, '--cat-to': gradient.to }}
                            >
                              <span className="sidebar-subcat-line" style={{ borderColor: `${gradient.from}30` }} />
                              <div className="sidebar-item-icon-wrap sidebar-subcat-icon" style={{ color: `${gradient.from}99` }}>
                                <ChildIcon />
                              </div>
                              <span className="sidebar-item-text">{child.name}</span>
                              {child.resource_count > 0 && (
                                <span className="sidebar-count">{child.resource_count}</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              // Flat fallback
              categories.map((cat, i) => {
                const IconComponent = (cat.icon && ICON_MAP[cat.icon]) || getCategoryIcon(cat.name, cat.slug);
                const gradient = categoryGradients[i % categoryGradients.length];
                const isActive = activeCategory === cat.slug;
                return (
                  <button
                    key={cat.id}
                    className={`sidebar-item ${isActive ? 'active' : ''}`}
                    onClick={() => { onCategoryClick(cat.slug); if (!pinned) setHovered(false); }}
                    style={{ '--cat-from': gradient.from, '--cat-to': gradient.to }}
                  >
                    <div className="sidebar-item-icon-wrap" style={{
                      background: isActive ? `linear-gradient(135deg, ${gradient.from}22, ${gradient.to}22)` : undefined,
                      color: isActive ? gradient.from : undefined
                    }}>
                      <IconComponent />
                    </div>
                    <span className="sidebar-item-text">{cat.name}</span>
                    {cat.resource_count > 0 && (
                      <span className="sidebar-count" style={{
                        color: isActive ? gradient.from : undefined,
                        background: isActive ? `${gradient.from}15` : undefined
                      }}>
                        {cat.resource_count}
                      </span>
                    )}
                    {isActive && (
                      <div className="sidebar-active-indicator" style={{ background: gradient.from, boxShadow: `0 0 12px ${gradient.from}` }} />
                    )}
                  </button>
                );
              })
            )}
          </div>

          <div className="sidebar-divider">
            <span className="sidebar-divider-text">{t('more')}</span>
          </div>

          <div className="sidebar-section sidebar-bottom-links">
            <Link to="/tools" className="sidebar-item" onClick={() => { if (!pinned) setHovered(false); }}>
              <div className="sidebar-item-icon-wrap sidebar-tools-icon">
                <FaTools />
              </div>
              <span className="sidebar-item-text">{t('toolbox')}</span>
            </Link>
            <a href="mailto:contact@gglancc.online" className="sidebar-item">
              <div className="sidebar-item-icon-wrap">
                <FaEnvelope />
              </div>
              <span className="sidebar-item-text">{t('contact')}</span>
            </a>
            <button className="sidebar-item" onClick={() => {}}>
              <div className="sidebar-item-icon-wrap">
                <FaInfoCircle />
              </div>
              <span className="sidebar-item-text">{t('about')}</span>
            </button>
          </div>
        </nav>
      </aside>
    </>
  );
}

export default Sidebar;
